import { Box } from '@mui/material'
import RoundedPanel, { Label } from 'common/RoundedPanel'
import React, { useEffect, useState } from 'react'
import { Datagrid, ListContextProvider, NumberField, TextField, useList, useRecordContext } from 'react-admin'
import { getCompanyDeadlines, CompanyDeadlinesResponse, DeadlineEntry, TatEntry } from 'api/company'
import 'styles.scss'

interface DeadlineRow {
  id: number
  wordRange: string
  timeToAccept: string
  deadline: string
}

const formatHours = (hours: number) => (hours === 1 ? '1 hour' : `${hours} hours`)

const formatWordRange = (from: number, to: number) =>
  `${from.toLocaleString('en-US')}-${to.toLocaleString('en-US')}`

const entriesToRows = (entries: DeadlineEntry[]): DeadlineRow[] =>
  entries.map((entry, index) => ({
    id: index + 1,
    wordRange: formatWordRange(entry.wordCountFrom, entry.wordCountTo),
    timeToAccept: formatHours(entry.responseTimeHours),
    deadline: formatHours(entry.deadlineHours)
  }))

const DeadlineTable = ({ data, title }: { data: DeadlineRow[]; title: string }) => {
  const listContext = useList({ data })

  return (
    <Box>
      <RoundedPanel centerText height="51px">
        <Label>{title}</Label>
      </RoundedPanel>
      <Box height={13} />
      <ListContextProvider value={listContext}>
        <Datagrid bulkActionButtons={false} className="assigments-datagrid small-row">
          <TextField source="wordRange" label="Number of words in a project" textAlign="right" sortable={false}/>
          <TextField source="timeToAccept" label="Time to accept a project" textAlign="right" sortable={false}/>
          <TextField source="deadline" label="Deadline" textAlign="right" sortable={false}/>
        </Datagrid>
      </ListContextProvider>
    </Box>
  )
}

interface TatRow {
  id: number
  wordRange: string
  totalTat: number
  redaction: number
  enhancement: number
  trustMining: number
}

const formatWordRangeTat = (from: number, to: number) =>
  `${from.toLocaleString('en-US')} - ${to.toLocaleString('en-US')}`

const tatEntriesToRows = (entries: TatEntry[]): TatRow[] =>
  entries.map((entry, index) => ({
    id: index + 1,
    wordRange: formatWordRangeTat(entry.wordCountFrom, entry.wordCountTo),
    totalTat: entry.totalTat,
    redaction: entry.redaction,
    enhancement: entry.enhancement,
    trustMining: entry.trustMining
  }))

const TatBusinessDaysTable = ({ data }: { data: TatRow[] }) => {
  const listContext = useList({ data })

  return (
    <Box>
      <RoundedPanel centerText height="51px" width="600px">
        <Label>TAT Business Days</Label>
      </RoundedPanel>
      <Box height={13} />
      <RoundedPanel width="600px" padding="0px">
        <ListContextProvider value={listContext}>
          <Datagrid bulkActionButtons={false} className="assigments-datagrid">
            <TextField source="wordRange" label="Wordcount" textAlign="right" sortable={false}/>
            <NumberField source="totalTat" label="Total TAT" locales="en-US" options={{ maximumFractionDigits: 1, minimumFractionDigits: 1 }} textAlign="right" sortable={false}/>
            <NumberField source="redaction" label="Redaction" locales="en-US" options={{ maximumFractionDigits: 1, minimumFractionDigits: 1 }} textAlign="right" sortable={false}/>
            <NumberField source="enhancement" label="Enhancement" locales="en-US" options={{ maximumFractionDigits: 1, minimumFractionDigits: 1 }} textAlign="right" sortable={false}/>
            <NumberField source="trustMining" label="Trust Mining" locales="en-US" options={{ maximumFractionDigits: 1, minimumFractionDigits: 1 }} textAlign="right" sortable={false}/>
          </Datagrid>
        </ListContextProvider>
      </RoundedPanel>
    </Box>
  )
}

const Deadlines = () => {
  const record = useRecordContext()
  const [data, setData] = useState<CompanyDeadlinesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!record?.id) return

    getCompanyDeadlines(record.id.toString())
      .then(setData)
      .catch((err) => setError(err?.message || 'Failed to load deadlines'))
      .finally(() => setLoading(false))
  }, [record?.id])

  if (!record) return null
  if (loading) return <Box><Box height={32} /><Label>Loading...</Label></Box>
  if (error) return <Box><Box height={32} /><Label>Error: {error}</Label></Box>
  if (!data) return null

  const redactionRows = entriesToRows(data.redaction)
  const enhancementRows = entriesToRows(data.enhancement)
  const trustMiningRows = entriesToRows(data.trustMining)
  const tatRows = tatEntriesToRows(data.tatBusinessDays ?? [])

  return (
    <Box>
      <Box height={32} />
      <Box className="panel deadlines-panel">
        <DeadlineTable data={redactionRows} title="Deadlines for Redactors" />
        <DeadlineTable data={enhancementRows} title="Deadlines for Enhancers and the Correction Stage" />
        <DeadlineTable data={trustMiningRows} title="Deadlines for Trust Miners" />
      </Box>
      {tatRows.length > 0 && (
        <>
          <Box height={32} />
          <Box className="deadlines-tat-section">
            <TatBusinessDaysTable data={tatRows} />
          </Box>
        </>
      )}
    </Box>
  )
}

export default Deadlines
